import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ActionIcon, Box, Button, Collapse, Group, Menu, Stack, Text, Tooltip } from '@mantine/core'
import {
  IconActivity,
  IconAdjustmentsHorizontal,
  IconAlertTriangle,
  IconAlertTriangleFilled,
  IconArchive,
  IconBookmark,
  IconBook,
  IconBug,
  IconCalendar,
  IconChartBar,
  IconChevronDown,
  IconChevronRight,
  IconDotsVertical,
  IconCircleCheck,
  IconFilter,
  IconInbox,
  IconLayoutDashboard,
  IconNotes,
  IconPackage,
  IconRefresh,
  IconRobot,
  IconSettings,
  IconShieldCheck,
  IconStar,
  IconUserCheck,
  IconUsers,
} from '@/components/icons'
import { useAuth } from '@/lib/auth-context'
import { api } from '@/lib/api'
import type { MyPermissionsResponse, ProjectRow, SavedFilterRow, TicketCountsResponse } from '@/types/api'
import {
  canAccessKnowledgeBase,
  canAccessOperatorQuickSettings,
  canAccessProjectSettings,
  hasPermission,
  loadMyPermissions,
  mergeWithDefaultPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import { formatSidebarNumber } from '@/lib/format-number'
import { buildSavedFilterSearch, getTicketsNavState } from '@/lib/tickets-nav'
import { ProjectIcon } from '@/components/sidebar/ProjectIcon'
import { SIDEBAR_NAV_LAYOUT, sidebarNavLinkStyles } from '@/components/sidebar/sidebar-nav.styles'
import { TabAwareNavLink } from '@/components/navigation/TabAwareNavLink'
import { ProjectQuickEditModal } from '@/components/projects/ProjectQuickEditModal'
import { ProjectDeleteModal } from '@/components/projects/ProjectDeleteModal'
import { PROJECTS_CHANGED_EVENT, TICKET_COUNTS_STALE_EVENT } from '@/lib/projects-events'

const SIDEBAR_SECTION_TICKETS = 'sidebarNavSection_tickets'
const SIDEBAR_SECTION_OPERATOR = 'sidebarNavSection_operator'
const SIDEBAR_SECTION_SETTINGS = 'sidebarNavSection_settings'

function readSectionExpanded(key: string): boolean {
  try {
    return localStorage.getItem(key) !== '0'
  } catch {
    return true
  }
}

function writeSectionExpanded(key: string, value: boolean) {
  try {
    localStorage.setItem(key, value ? '1' : '0')
  } catch {
    /* ignore */
  }
}

function SidebarSectionHeader({
  label,
  expanded,
  onToggle,
  mt = 0,
}: {
  label: string
  expanded: boolean
  onToggle: () => void
  mt?: number | string
}) {
  return (
    <Button
      type="button"
      variant="subtle"
      color="gray"
      size="compact-xs"
      fullWidth
      justify="space-between"
      mt={mt}
      px={6}
      py={6}
      h="auto"
      fz={11}
      fw={700}
      tt="uppercase"
      lts={0.08}
      c="dimmed"
      aria-expanded={expanded}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onToggle()
      }}
      rightSection={expanded ? <IconChevronDown size={12} stroke={1.5} /> : <IconChevronRight size={12} stroke={1.5} />}
      styles={{
        root: {
          fontWeight: 700,
          borderRadius: 'var(--mantine-radius-lg)',
          background: 'transparent',
          border: '1px solid transparent',
        },
        inner: { justifyContent: 'space-between', width: '100%' },
        label: { flex: 1, textAlign: 'left', letterSpacing: '0.08em' },
      }}
    >
      {label}
    </Button>
  )
}

export function SidebarNav({ collapsed }: { collapsed: boolean }) {
  const location = useLocation()
  const { user } = useAuth()
  const nav = getTicketsNavState(location.pathname, location.search)

  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [counts, setCounts] = useState<TicketCountsResponse | null>(null)
  const [filters, setFilters] = useState<SavedFilterRow[]>([])
  const [editProject, setEditProject] = useState<ProjectRow | null>(null)
  const [deleteProject, setDeleteProject] = useState<ProjectRow | null>(null)

  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('collapsedProjects')
      if (raw != null) return new Set(JSON.parse(raw) as string[])
    } catch {
      /* ignore */
    }
    return new Set()
  })

  const prevProjectIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])

  const refreshTicketCounts = useCallback(async () => {
    try {
      const fc = await api.ticketCounts().catch(() => null)
      if (fc) setCounts(fc)
    } catch {
      /* ignore */
    }
  }, [])

  const loadSidebarProjects = useCallback(async () => {
    try {
      const [pl, fc, fl] = await Promise.all([
        api.projects(),
        api.ticketCounts().catch(() => null),
        api.filtersList().catch(() => ({ filters: [] as SavedFilterRow[] })),
      ])
      const sorted = [...pl].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
      setProjects(sorted)
      if (fc) setCounts(fc)
      setFilters(fl.filters ?? [])

      try {
        if (localStorage.getItem('collapsedProjects') === null) {
          const all = new Set(sorted.map((p) => p.id))
          setCollapsedProjects(all)
          localStorage.setItem('collapsedProjects', JSON.stringify([...all]))
        }
        prevProjectIdsRef.current = new Set(sorted.map((p) => p.id))
      } catch {
        /* ignore */
      }
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    void loadSidebarProjects()
  }, [loadSidebarProjects])

  useEffect(() => {
    const h = () => void loadSidebarProjects()
    window.addEventListener(PROJECTS_CHANGED_EVENT, h)
    return () => window.removeEventListener(PROJECTS_CHANGED_EVENT, h)
  }, [loadSidebarProjects])

  useEffect(() => {
    const h = () => void refreshTicketCounts()
    window.addEventListener(TICKET_COUNTS_STALE_EVENT, h)
    return () => window.removeEventListener(TICKET_COUNTS_STALE_EVENT, h)
  }, [refreshTicketCounts])

  /** Появившиеся позже проекты по умолчанию свёрнуты (если уже есть сохранённые настройки). */
  useEffect(() => {
    if (projects.length === 0) return

    const prev = prevProjectIdsRef.current
    const nowIds = projects.map((p) => p.id)

    if (prev.size === 0) {
      prevProjectIdsRef.current = new Set(nowIds)
      return
    }

    const newIds = nowIds.filter((id) => !prev.has(id))
    if (newIds.length > 0) {
      setCollapsedProjects((s) => {
        const next = new Set(s)
        for (const id of newIds) next.add(id)
        try {
          localStorage.setItem('collapsedProjects', JSON.stringify([...next]))
        } catch {
          /* ignore */
        }
        return next
      })
    }

    prevProjectIdsRef.current = new Set(nowIds)
  }, [projects])

  const toggleProject = useCallback((id: string) => {
    setCollapsedProjects((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      try {
        localStorage.setItem('collapsedProjects', JSON.stringify([...next]))
      } catch {
        /* ignore */
      }
      return next
    })
  }, [])

  const [sectionTicketsOpen, setSectionTicketsOpen] = useState(() => readSectionExpanded(SIDEBAR_SECTION_TICKETS))
  const [sectionOperatorOpen, setSectionOperatorOpen] = useState(() => readSectionExpanded(SIDEBAR_SECTION_OPERATOR))
  const [sectionSettingsOpen, setSectionSettingsOpen] = useState(() => readSectionExpanded(SIDEBAR_SECTION_SETTINGS))

  const toggleSectionTickets = useCallback(() => {
    setSectionTicketsOpen((v) => {
      const next = !v
      writeSectionExpanded(SIDEBAR_SECTION_TICKETS, next)
      return next
    })
  }, [])
  const toggleSectionOperator = useCallback(() => {
    setSectionOperatorOpen((v) => {
      const next = !v
      writeSectionExpanded(SIDEBAR_SECTION_OPERATOR, next)
      return next
    })
  }, [])
  const toggleSectionSettings = useCallback(() => {
    setSectionSettingsOpen((v) => {
      const next = !v
      writeSectionExpanded(SIDEBAR_SECTION_SETTINGS, next)
      return next
    })
  }, [])

  const p = mergeWithDefaultPermissions(perm, user?.role)
  const can = (key: string) => hasPermission(p, key, user?.role)
  const canOpenWorkspaceSettings =
    can('access_settings') ||
    can('manage_projects') ||
    can('manage_workspace_settings') ||
    can('manage_triggers') ||
    !!p.can_manage_permissions
  const isStaff = (user?.role || '').toLowerCase() !== 'customer'
  const ai = counts?.ai ?? {}
  const byProject = counts?.by_project ?? {}
  const anyEmergencyActive = Object.values(byProject).some((c) => c?.emergency_mode_active)

  const favorites = useMemo(() => filters.filter((f) => f.is_favorite), [filters])

  const navBase = useMemo(() => sidebarNavLinkStyles(collapsed), [collapsed])
  const navTopEdge = useMemo(() => {
    if (collapsed) return navBase
    return {
      ...navBase,
      root: {
        ...navBase.root,
        borderTopLeftRadius: 'var(--mantine-radius-xl)',
        borderTopRightRadius: 'var(--mantine-radius-xl)',
      },
    }
  }, [collapsed, navBase])

  const nl = useCallback(
    (active: boolean, options?: { topEdge?: boolean }) => ({
      variant: 'light' as const,
      color: 'brand' as const,
      active,
      noWrap: true,
      styles: active && options?.topEdge ? navTopEdge : navBase,
    }),
    [navBase, navTopEdge],
  )

  const wrapCollapsed = (label: string, node: ReactNode) => {
    if (!collapsed) return node
    return (
      <Tooltip label={label} position="right" withArrow>
        {node}
      </Tooltip>
    )
  }

  const { chevronCol, chevronGap, subIndent, rowMinHeight } = SIDEBAR_NAV_LAYOUT

  return (
    <Stack gap={0}>
      <ProjectQuickEditModal opened={editProject != null} project={editProject} onClose={() => setEditProject(null)} />
      <ProjectDeleteModal
        opened={deleteProject != null}
        project={deleteProject}
        onClose={() => setDeleteProject(null)}
        onDeleted={() => setDeleteProject(null)}
      />
      {isStaff &&
        wrapCollapsed(
          'Дашборд',
          <TabAwareNavLink
            to="/dashboard"
            label={collapsed ? undefined : 'Дашборд'}
            leftSection={<IconLayoutDashboard size={16} stroke={1.5} />}
            {...nl(location.pathname === '/dashboard', { topEdge: true })}
          />,
        )}

      {!collapsed && (
        <SidebarSectionHeader label="Обращения" expanded={sectionTicketsOpen} onToggle={toggleSectionTickets} mt={8} />
      )}

      {collapsed || sectionTicketsOpen ? (
        <Stack gap={0}>
          {can('access_tickets') &&
            wrapCollapsed(
              'Все обращения',
              <TabAwareNavLink
                to="/tickets"
                label={collapsed ? undefined : 'Все обращения'}
                leftSection={<IconInbox size={16} stroke={1.5} />}
                {...nl(nav.isAllTicketsActive, { topEdge: !isStaff })}
              />,
            )}

          {can('access_tickets') &&
            projects.map((project) => {
              const c = byProject[project.id] ?? {}
              const total = c.total_tickets ?? 0
              const projectMatches = nav.urlProject === project.slug || nav.urlProject === project.id
              const isMainActive =
                projectMatches &&
                nav.urlStatus !== 'resolved' &&
                nav.urlStatus !== 'closed' &&
                nav.urlStatus !== 'emergency'
              const isResolvedActive = projectMatches && nav.urlStatus === 'resolved'
              const isClosedActive = projectMatches && nav.urlStatus === 'closed'
              const isEmergencyActive = projectMatches && nav.urlStatus === 'emergency'
              const isProjCollapsed = collapsedProjects.has(project.id)
              const emergencyOn = !!c.emergency_mode_active

              return (
                <Box key={project.id}>
                  <Group gap={chevronGap} wrap="nowrap" align="center" justify="flex-start">
                    {!collapsed ? (
                      <Box
                        w={chevronCol}
                        miw={chevronCol}
                        h={rowMinHeight}
                        style={{
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="gray"
                          onClick={() => toggleProject(project.id)}
                          aria-label={isProjCollapsed ? 'Развернуть' : 'Свернуть'}
                        >
                          {isProjCollapsed ? <IconChevronRight size={12} /> : <IconChevronDown size={12} />}
                        </ActionIcon>
                      </Box>
                    ) : null}
                    {wrapCollapsed(
                      `${project.name} (${total})`,
                      <TabAwareNavLink
                        to={`/tickets?project=${encodeURIComponent(project.slug)}`}
                        label={collapsed ? undefined : project.name}
                        leftSection={<ProjectIcon project={project} boxSize={16} emergency={emergencyOn} />}
                        rightSection={
                          collapsed ? undefined : (
                            <Text component="span" fz={12} c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                              ({formatSidebarNumber(total)})
                            </Text>
                          )
                        }
                        variant="light"
                        color="brand"
                        active={!!isMainActive}
                        noWrap
                        styles={{
                          ...navBase,
                          root: { ...navBase.root, flex: 1, minWidth: 0 },
                        }}
                      />,
                    )}
                    {!collapsed && canAccessProjectSettings(p) ? (
                      <Menu shadow="md" width={200} position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            color="gray"
                            aria-label={`Действия: ${project.name}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconDotsVertical size={14} stroke={1.5} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            component={Link}
                            to={`/settings?tab=projects&project=${encodeURIComponent(project.id)}`}
                          >
                            Настроить
                          </Menu.Item>
                          <Menu.Item onClick={() => setEditProject(project)}>Изменить</Menu.Item>
                          <Menu.Item color="red" onClick={() => setDeleteProject(project)}>
                            Удалить
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    ) : null}
                  </Group>
                  {!collapsed && (
                    <Collapse in={!isProjCollapsed}>
                      <Stack gap={0} pl={subIndent} mt={0}>
                        <TabAwareNavLink
                          to={`/tickets?project=${encodeURIComponent(project.slug)}&status=resolved`}
                          label="Решённые"
                          rightSection={
                            <Text component="span" fz={12} c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                              ({formatSidebarNumber(c.resolved ?? 0)})
                            </Text>
                          }
                          leftSection={<IconCircleCheck size={14} stroke={1.5} />}
                          {...nl(isResolvedActive)}
                        />
                        <TabAwareNavLink
                          to={`/tickets?project=${encodeURIComponent(project.slug)}&status=closed`}
                          label="Закрытые"
                          rightSection={
                            <Text component="span" fz={12} c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                              ({formatSidebarNumber(c.closed ?? 0)})
                            </Text>
                          }
                          leftSection={<IconArchive size={14} stroke={1.5} />}
                          {...nl(isClosedActive)}
                        />
                        {emergencyOn && (
                          <TabAwareNavLink
                            to={`/tickets?project=${encodeURIComponent(project.slug)}&status=emergency`}
                            label="Авария"
                            rightSection={
                              <Text component="span" fz={12} c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                                ({formatSidebarNumber(c.emergency ?? 0)})
                              </Text>
                            }
                            leftSection={<IconAlertTriangle size={14} stroke={1.5} />}
                            {...nl(isEmergencyActive)}
                          />
                        )}
                      </Stack>
                    </Collapse>
                  )}
                </Box>
              )
            })}

          {can('access_tickets') &&
            wrapCollapsed(
              'Закладки',
              <TabAwareNavLink
                to="/bookmarks"
                label={collapsed ? undefined : 'Закладки'}
                leftSection={<IconBookmark size={16} stroke={1.5} />}
                {...nl(location.pathname === '/bookmarks')}
              />,
            )}

          {wrapCollapsed(
            'Аварийный режим',
            <TabAwareNavLink
              to="/emergency-mode"
              label={collapsed ? undefined : 'Аварийный режим'}
              leftSection={
                anyEmergencyActive ? (
                  <IconAlertTriangleFilled size={16} stroke={1.5} color="var(--mantine-color-red-6)" />
                ) : (
                  <IconAlertTriangle size={16} stroke={1.5} />
                )
              }
              {...nl(location.pathname === '/emergency-mode')}
              styles={
                anyEmergencyActive
                  ? { ...navBase, label: { ...navBase.label, color: 'var(--mantine-color-red-6)', fontWeight: 600 } }
                  : navBase
              }
            />,
          )}

          {can('access_anomalies') &&
            wrapCollapsed(
              'Аномалии',
              <TabAwareNavLink
                to="/anomalies"
                label={collapsed ? undefined : 'Аномалии'}
                leftSection={<IconActivity size={16} stroke={1.5} />}
                {...nl(location.pathname === '/anomalies')}
              />,
            )}

          {can('access_ai_tickets') &&
            wrapCollapsed(
              'Мозгобот',
              <TabAwareNavLink
                to="/ai-tickets"
                label={collapsed ? undefined : 'Мозгобот'}
                rightSection={
                  collapsed ? undefined : (
                    <Text component="span" fz={12} c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      {formatSidebarNumber(ai.total_tickets ?? 0)} / {formatSidebarNumber(ai.require_processing ?? 0)}
                    </Text>
                  )
                }
                leftSection={<IconRobot size={16} stroke={1.5} />}
                {...nl(location.pathname === '/ai-tickets')}
              />,
            )}

          {!collapsed &&
            favorites.map((f, index) => {
              const qs = buildSavedFilterSearch(f, projects)
              const active = location.pathname === '/tickets' && nav.urlFilterName === f.name
              return (
                <TabAwareNavLink
                  key={f.id}
                  to={`/tickets?${qs}`}
                  label={f.name}
                  leftSection={
                    <Box
                      w={18}
                      h={18}
                      style={{
                        borderRadius: 999,
                        background: f.color || 'var(--mantine-color-brand-filled)',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Box>
                  }
                  {...nl(active)}
                />
              )
            })}
        </Stack>
      ) : null}

      {!collapsed && (
        <SidebarSectionHeader
          label="Оператор"
          expanded={sectionOperatorOpen}
          onToggle={toggleSectionOperator}
          mt="sm"
        />
      )}

      {collapsed || sectionOperatorOpen ? (
        <Stack gap={0}>
          {canAccessOperatorQuickSettings(p) &&
            wrapCollapsed(
              'Быстрые настройки',
              <TabAwareNavLink
                to="/operator/quick-settings"
                label={collapsed ? undefined : 'Быстрые настройки'}
                leftSection={<IconAdjustmentsHorizontal size={16} stroke={1.5} />}
                {...nl(location.pathname === '/operator/quick-settings')}
              />,
            )}

          {can('access_tickets') &&
            wrapCollapsed(
              'Фильтры',
              <TabAwareNavLink
                to="/filters"
                label={collapsed ? undefined : 'Фильтры'}
                leftSection={<IconFilter size={16} stroke={1.5} />}
                {...nl(location.pathname === '/filters')}
              />,
            )}

          {wrapCollapsed(
            'Табель',
            <TabAwareNavLink
              to="/schedules"
              label={collapsed ? undefined : 'Табель'}
              leftSection={<IconCalendar size={16} stroke={1.5} />}
              {...nl(location.pathname === '/schedules')}
            />,
          )}

          {wrapCollapsed(
            'Макросы',
            <TabAwareNavLink
              to="/macros"
              label={collapsed ? undefined : 'Макросы'}
              leftSection={<IconNotes size={16} stroke={1.5} />}
              {...nl(location.pathname === '/macros')}
            />,
          )}

          {can('access_actualization') &&
            wrapCollapsed(
              'Актуализация',
              <TabAwareNavLink
                to="/actualization"
                label={collapsed ? undefined : 'Актуализация'}
                leftSection={<IconRefresh size={16} stroke={1.5} />}
                {...nl(location.pathname === '/actualization')}
              />,
            )}

          {can('access_reviews') &&
            wrapCollapsed(
              'Оценки и отзывы',
              <TabAwareNavLink
                to="/reviews"
                label={collapsed ? undefined : 'Оценки и отзывы'}
                leftSection={<IconStar size={16} stroke={1.5} />}
                {...nl(location.pathname === '/reviews')}
              />,
            )}

          {can('access_quality_arm') &&
            wrapCollapsed(
              'АРМ Качества',
              <TabAwareNavLink
                to="/quality"
                label={collapsed ? undefined : 'АРМ Качества'}
                leftSection={<IconShieldCheck size={16} stroke={1.5} />}
                {...nl(location.pathname === '/quality')}
              />,
            )}

          {(can('access_debug') || can('access_ai_audit_log')) &&
            wrapCollapsed(
              'Отладка',
              <TabAwareNavLink
                to="/debug"
                label={collapsed ? undefined : 'Отладка'}
                leftSection={<IconBug size={16} stroke={1.5} />}
                {...nl(location.pathname === '/debug')}
              />,
            )}

          {can('access_exports') &&
            wrapCollapsed(
              'Выгрузки',
              <TabAwareNavLink
                to="/exports"
                label={collapsed ? undefined : 'Выгрузки'}
                leftSection={<IconArchive size={16} stroke={1.5} />}
                {...nl(location.pathname === '/exports')}
              />,
            )}
        </Stack>
      ) : null}

      {!collapsed && (
        <SidebarSectionHeader
          label="Настройки"
          expanded={sectionSettingsOpen}
          onToggle={toggleSectionSettings}
          mt="sm"
        />
      )}

      {collapsed || sectionSettingsOpen ? (
        <Stack gap={0}>
          {can('access_installers') &&
            wrapCollapsed(
              'Установщики',
              <TabAwareNavLink
                to="/installers"
                label={collapsed ? undefined : 'Установщики'}
                leftSection={<IconPackage size={16} stroke={1.5} />}
                {...nl(location.pathname === '/installers')}
              />,
            )}

          {can('access_users') &&
            wrapCollapsed(
              'Пользователи',
              <TabAwareNavLink
                to="/users"
                label={collapsed ? undefined : 'Пользователи'}
                leftSection={<IconUsers size={16} stroke={1.5} />}
                {...nl(location.pathname === '/users')}
              />,
            )}

          {can('access_operators') &&
            wrapCollapsed(
              'Операторы',
              <TabAwareNavLink
                to="/operators"
                label={collapsed ? undefined : 'Операторы'}
                leftSection={<IconUserCheck size={16} stroke={1.5} />}
                {...nl(location.pathname === '/operators')}
              />,
            )}

          {can('access_analytics') &&
            wrapCollapsed(
              'Аналитика',
              <TabAwareNavLink
                to="/analytics"
                label={collapsed ? undefined : 'Аналитика'}
                leftSection={<IconChartBar size={16} stroke={1.5} />}
                {...nl(location.pathname === '/analytics')}
              />,
            )}

          {can('access_analytics') &&
            wrapCollapsed(
              'Аналитика New',
              <TabAwareNavLink
                to="/analytics-new"
                label={collapsed ? undefined : 'Аналитика New'}
                leftSection={<IconChartBar size={16} stroke={1.5} />}
                {...nl(location.pathname === '/analytics-new')}
              />,
            )}

          {canOpenWorkspaceSettings &&
            wrapCollapsed(
              'Настройки',
              <TabAwareNavLink
                to="/settings"
                label={collapsed ? undefined : 'Настройки'}
                leftSection={<IconSettings size={16} stroke={1.5} />}
                {...nl(location.pathname === '/settings')}
              />,
            )}

          {canAccessKnowledgeBase(p, user?.role) &&
            wrapCollapsed(
              'База знаний',
              <TabAwareNavLink
                to="/knowledge-base"
                label={collapsed ? undefined : 'База знаний'}
                leftSection={<IconBook size={16} stroke={1.5} />}
                {...nl(location.pathname === '/knowledge-base')}
              />,
            )}

          {can('access_activity_log') &&
            wrapCollapsed(
              'Лог действий',
              <TabAwareNavLink
                to="/activity-log"
                label={collapsed ? undefined : 'Лог действий'}
                leftSection={<IconActivity size={16} stroke={1.5} />}
                {...nl(location.pathname === '/activity-log')}
              />,
            )}
        </Stack>
      ) : null}
    </Stack>
  )
}
